import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import { Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { levelLabels, plural } from "../../program-access/access-model";
import { LevelSelect } from "../../program-access/components/level-select";

import type { UUID } from "../../../utils/uuid";
import type { Level } from "../../program-access/access-model";
import type { ProgramNode } from "../../program-tree/program-tree";
import type { MoveAccess } from "../program-move";
import type { FC } from "react";

/** The levels that the admin picks in the dialog. A contributor with none keeps the level of today. */
export type ChosenLevels = Partial<Record<UUID, Level>>;

const AccessRows: FC<{
  rows: MoveAccess[];
  parent: ProgramNode | undefined;
  chosen: ChosenLevels;
  onChosen: (chosen: ChosenLevels) => void;
}> = ({ rows, parent, chosen, onChosen }) => (
  <Stack component="ul" spacing={1.5} sx={{ m: 0, p: 0, listStyle: "none" }}>
    {rows.map(({ person, today, below, destination }) => {
      const level = chosen[person.id] ?? today;
      return (
        <Stack
          key={person.id}
          component="li"
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center" }}
        >
          <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>{person.initials}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {person.name}
              </Typography>
              {level === today ? null : (
                <Chip size="small" color="primary" label="Changed" sx={{ fontSize: 11 }} />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {`Today: ${levelLabels[today]}`}
              {today === "none" && below ? ` (access to ${plural(below, "folder")} inside)` : ""}
              {parent
                ? ` · ${parent.title} would give ${levelLabels[destination]}`
                : " · New programs give No access by default"}
            </Typography>
          </Box>
          <LevelSelect
            label={`Access for ${person.name} after the move`}
            level={level}
            onChange={(next) => onChosen({ ...chosen, [person.id]: next })}
          />
        </Stack>
      );
    })}
  </Stack>
);

/**
 * Who has access to the program after the move, in step 2 of the Move dialog. Each contributor keeps
 * their level of today, unless the admin picks another one. Contributors who can see the new parent
 * but not the program get no access, unless the admin picks a level.
 */
export const MoveAccessSection: FC<{
  program: ProgramNode;
  /** The new parent, or nothing for the top level. */
  parent: ProgramNode | undefined;
  listed: MoveAccess[];
  newcomers: MoveAccess[];
  chosen: ChosenLevels;
  onChosen: (chosen: ChosenLevels) => void;
}> = ({ program, parent, listed, newcomers, chosen, onChosen }) => {
  const kind = program.parent_id ? "folder" : "program";
  const everyone = [...listed, ...newcomers];
  const changed = everyone.some(({ person, today }) => (chosen[person.id] ?? today) !== today);

  return (
    <Stack component="section" aria-labelledby="move-access-title" spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography id="move-access-title" variant="subtitle1" component="h3" sx={{ mr: "auto" }}>
          Access after the move
        </Typography>
        {parent && everyone.length ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              onChosen(
                Object.fromEntries(
                  everyone.map(({ person, destination }) => [person.id, destination]),
                ),
              )
            }
            data-analytics-id="program-move-match-access"
          >
            Match {parent.title}
          </Button>
        ) : null}
        {changed ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onChosen({})}
            data-analytics-id="program-move-keep-access"
          >
            Keep current access
          </Button>
        ) : null}
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {listed.length
          ? "Everyone keeps the access they have today, so nothing changes for them unless you change it here. Any exceptions set on folders inside are kept too."
          : "Nobody else has access today."}
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", color: "text.secondary" }}>
        <AdminPanelSettingsOutlined sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Admins always have Create and edit. This can’t be changed.
        </Typography>
      </Stack>
      {listed.length ? (
        <AccessRows rows={listed} parent={parent} chosen={chosen} onChosen={onChosen} />
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {newcomers.length
            ? `No contributors can see this ${kind} today.`
            : `Only admins can see this ${kind} today, and that won’t change.`}
        </Typography>
      )}
      {parent && newcomers.length ? (
        <>
          <Box>
            <Typography variant="subtitle2" component="h4">
              People who can see {parent.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              They don’t have access to “{program.title}” today, so they stay at No access unless
              you change it here.
            </Typography>
          </Box>
          <AccessRows rows={newcomers} parent={parent} chosen={chosen} onChosen={onChosen} />
        </>
      ) : null}
    </Stack>
  );
};
